import React, { useMemo, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { exportMessageAsPdf } from '../utils/pdfExport.js';

const CHART_COLORS = ['#568ded', '#34d399', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

/**
 * Try to parse a ```chart code block into a recharts component.
 * Expected JSON format:
 * { "type": "bar"|"pie"|"line", "title": "...", "data": [...], "dataKey": "...", "nameKey": "..." }
 */
function ChartBlock({ value }) {
  let config;
  try {
    config = JSON.parse(value);
  } catch {
    return React.createElement('pre', null, React.createElement('code', null, value));
  }

  const { type, data, title } = config;
  if (!Array.isArray(data) || data.length === 0) {
    return React.createElement('pre', null, React.createElement('code', null, value));
  }

  const dataKey = config.dataKey || config.valueKey || Object.keys(data[0]).find(k => typeof data[0][k] === 'number') || 'value';
  const nameKey = config.nameKey || config.labelKey || Object.keys(data[0]).find(k => typeof data[0][k] === 'string') || 'name';

  // Check if labels are long and need rotation
  const maxLabelLen = Math.max(...data.map(d => String(d[nameKey] || '').length));
  const needsRotation = maxLabelLen > 8 || data.length > 6;
  const xAxisProps = {
    dataKey: nameKey,
    stroke: 'rgba(255,255,255,0.5)',
    fontSize: needsRotation ? 9 : 11,
    interval: 0, // Show ALL labels
    ...(needsRotation ? { angle: -35, textAnchor: 'end', height: 60 } : {})
  };

  // Compute YAxis width to fit the widest value label
  const allNumericKeys = Object.keys(data[0]).filter(k => typeof data[0][k] === 'number');
  const maxYValue = Math.max(...data.flatMap(d => allNumericKeys.map(k => Math.abs(d[k] || 0))));
  const maxYChars = String(Math.round(maxYValue)).length;
  const yAxisWidth = Math.max(35, maxYChars * 9 + 12);
  const yAxisProps = {
    stroke: 'rgba(255,255,255,0.5)',
    fontSize: 11,
    width: yAxisWidth,
  };

  const legendProps = {
    wrapperStyle: { fontSize: 11, paddingTop: 4, lineHeight: '18px' }
  };
  const chartH = needsRotation ? 280 : 240;

  const wrapper = (children) =>
    React.createElement('div', { className: 'message__chart' },
      title ? React.createElement('p', { className: 'message__chart-title' }, title) : null,
      React.createElement(ResponsiveContainer, { width: '100%', height: chartH }, children)
    );

  if (type === 'pie') {
    // Reduce radius when many items to leave room for labels
    const radius = data.length > 6 ? 60 : data.length > 4 ? 70 : 80;
    return wrapper(
      React.createElement(PieChart, null,
        React.createElement(Pie, {
          data, dataKey, nameKey,
          cx: '50%', cy: '45%', outerRadius: radius,
          label: (entry) => {
            const name = entry[nameKey] || '';
            return name.length > 18 ? name.slice(0, 16) + '…' : name;
          },
          labelLine: true,
          fontSize: data.length > 6 ? 9 : 11,
        },
          data.map((_, i) => React.createElement(Cell, { key: i, fill: CHART_COLORS[i % CHART_COLORS.length] }))
        ),
        React.createElement(Tooltip, null),
        React.createElement(Legend, legendProps)
      )
    );
  }

  if (type === 'line') {
    const lineKeys = Object.keys(data[0]).filter(k => typeof data[0][k] === 'number');
    return wrapper(
      React.createElement(LineChart, { data, margin: { top: 5, right: 30, left: 0, bottom: 5 } },
        React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.1)' }),
        React.createElement(XAxis, xAxisProps),
        React.createElement(YAxis, yAxisProps),
        React.createElement(Tooltip, { contentStyle: { background: '#0a1628', border: '1px solid rgba(255,255,255,0.1)' } }),
        React.createElement(Legend, legendProps),
        ...lineKeys.map((key, i) =>
          React.createElement(Line, { key, type: 'monotone', dataKey: key, stroke: CHART_COLORS[i % CHART_COLORS.length], strokeWidth: 2 })
        )
      )
    );
  }

  // Default: bar chart
  const barKeys = Object.keys(data[0]).filter(k => typeof data[0][k] === 'number');
  return wrapper(
    React.createElement(BarChart, { data, margin: { top: 5, right: 30, left: 0, bottom: 5 } },
      React.createElement(CartesianGrid, { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.1)' }),
      React.createElement(XAxis, xAxisProps),
      React.createElement(YAxis, yAxisProps),
      React.createElement(Tooltip, { contentStyle: { background: '#0a1628', border: '1px solid rgba(255,255,255,0.1)' } }),
      React.createElement(Legend, legendProps),
      ...barKeys.map((key, i) =>
        React.createElement(Bar, { key, dataKey: key, fill: CHART_COLORS[i % CHART_COLORS.length], radius: [4, 4, 0, 0] })
      )
    )
  );
}

/** Custom code block renderer: intercepts ```chart blocks for chart rendering */
function CodeRenderer({ node, inline, className, children, ...props }) {
  const match = /language-(\w+)/.exec(className || '');
  const lang = match ? match[1] : '';

  if (!inline && lang === 'chart') {
    const value = String(children).replace(/\n$/, '');
    return React.createElement(ChartBlock, { value });
  }

  if (inline) {
    return React.createElement('code', { className, ...props }, children);
  }

  return React.createElement('pre', null,
    React.createElement('code', { className, ...props }, children)
  );
}

/**
 * Loading indicator for agent tool actions
 */
function AgentActionIndicator({ actions }) {
  if (!actions || actions.length === 0) return null;

  return React.createElement('article', { className: 'message assistant agent-thinking' },
    React.createElement('strong', null, 'Sigmund'),
    React.createElement('div', { className: 'agent-actions' },
      actions.map((action, idx) =>
        React.createElement('div', {
          key: idx,
          className: 'agent-action-item' + (idx === actions.length - 1 ? ' active' : ' done')
        },
          React.createElement('span', { className: 'agent-action-dot' }),
          React.createElement('span', { className: 'agent-action-text' }, action.text),
          action.done
            ? React.createElement('span', { className: 'agent-action-check' }, '✓')
            : React.createElement('span', { className: 'agent-action-spinner' })
        )
      )
    )
  );
}

/**
 * Download button that appears on hover for assistant messages
 */
function DownloadButton({ onClick, exporting }) {
  return React.createElement(
    'button',
    {
      type: 'button',
      className: 'message__download-btn',
      onClick,
      disabled: exporting,
      title: 'Download as PDF'
    },
    exporting
      ? React.createElement('span', { className: 'message__download-spinner' })
      : React.createElement('svg', {
        width: 14, height: 14, viewBox: '0 0 24 24',
        fill: 'none', stroke: 'currentColor', strokeWidth: 2,
        strokeLinecap: 'round', strokeLinejoin: 'round'
      },
        React.createElement('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }),
        React.createElement('polyline', { points: '7 10 12 15 17 10' }),
        React.createElement('line', { x1: 12, y1: 15, x2: 12, y2: 3 })
      )
  );
}

const markdownComponents = { code: CodeRenderer };

export function MessageFeed({ messages, agentActions, className = 'messages' }) {
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const messageRefs = useRef({});
  const [exportingIdx, setExportingIdx] = React.useState(null);

  const handleDownload = useCallback(async (idx) => {
    const el = messageRefs.current[idx];
    if (!el) return;
    setExportingIdx(idx);
    try {
      await exportMessageAsPdf(el, idx);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExportingIdx(null);
    }
  }, []);

  return React.createElement(
    'div',
    { className },
    messages.map((msg, idx) =>
      React.createElement(
        'article',
        {
          key: msg.id || idx,
          className: 'message ' + msg.role,
          ref: msg.role === 'assistant' ? (el) => { messageRefs.current[idx] = el; } : undefined
        },
        React.createElement('strong', null, msg.role === 'user' ? 'You' : 'Sigmund'),
        msg.role === 'assistant' && msg.content
          ? React.createElement(DownloadButton, {
            onClick: () => handleDownload(idx),
            exporting: exportingIdx === idx
          })
          : null,
        React.createElement(
          'div',
          { className: 'message__body' },
          msg.role === 'user'
            ? React.createElement('p', null, msg.content)
            : React.createElement(ReactMarkdown, {
              remarkPlugins,
              components: markdownComponents
            }, msg.content || '')
        )
      )
    ),
    React.createElement(AgentActionIndicator, { actions: agentActions })
  );
}
